// A PDF in a window over the page, instead of a new browser tab (Ryan, Oct 1 2026, on
// the Work Order Billing board). Give it either a direct `url` (an upload) or an API
// `fetchPath` that needs the session token (a drawn invoice); the bytes are fetched into
// a blob and shown in a frame. "Open in a tab ↗" is still there for printing or saving.
import React, { useEffect, useState } from 'react';
import { API_BASE, getToken } from '../services/apiClient';

// fileName: what a saved copy is called ("2631 Submittal 01 ITS Vaults.pdf"). The frame
// shows a blob, and a blob has no name — the viewer's own save button and a new tab both
// save a random string — so the window has its own Download (named properly) and Print, and
// the viewer's toolbar is hidden so there is one way to save and it is the right one.
export default function PdfWindow({ url = null, fetchPath = null, title = 'Document', fileName = null, onClose }) {
  const frameRef = React.useRef(null);
  const saveAs = (() => {
    const base = String(fileName || title || 'Document').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Document';
    return /\.pdf$/i.test(base) ? base : base + '.pdf';
  })();
  const printIt = () => { try { const w = frameRef.current && frameRef.current.contentWindow; if (w) { w.focus(); w.print(); } } catch { window.open(src, '_blank'); } };
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState('');

  // Both kinds are fetched into a blob: the API answers with X-Frame-Options: deny, so an
  // upload's own URL will not show in a frame, but a blob of the same bytes will.
  useEffect(() => {
    if (!url && !fetchPath) return undefined;
    let live = true, made = null;
    fetch(url || `${API_BASE}${fetchPath}`, url ? {} : { headers: { 'x-worker-token': getToken() } })
      .then(async r => {
        if (!r.ok) { let m = `Could not draw it (${r.status}).`; try { const j = await r.json(); if (j && j.error) m = j.error; } catch {} throw new Error(m); }
        return r.blob();
      })
      .then(b => { made = URL.createObjectURL(b); if (live) setSrc(made); })
      .catch(e => { if (live) setErr(e.message || 'Could not open it.'); });
    return () => { live = false; if (made) setTimeout(() => URL.revokeObjectURL(made), 1000); };
  }, [url, fetchPath]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 6500, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 980, height: 'min(94vh, 1100px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ background: '#1e3a5f', color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 800, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>🧾 {title}</span>
          {src && <a href={src} download={saveAs} title={`Saves as “${saveAs}”`} style={{ background: '#fff', color: '#1e3a5f', fontSize: 12.5, fontWeight: 800, textDecoration: 'none', whiteSpace: 'nowrap', padding: '6px 12px', borderRadius: 8 }}>⬇ Download</a>}
          {src && <button onClick={printIt} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap', padding: '6px 12px', borderRadius: 8, cursor: 'pointer' }}>🖨 Print</button>}
          {(src || url) && <a href={url || src} target="_blank" rel="noreferrer" style={{ color: '#fff', fontSize: 12.5, fontWeight: 700, textDecoration: 'underline', whiteSpace: 'nowrap' }}>Open in a tab ↗</a>}
          <button onClick={onClose} aria-label="Close" style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 16, fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: '#e5e7eb' }}>
          {err ? <div style={{ padding: 30, color: '#b91c1c', fontSize: 14, fontWeight: 600 }}>{err}</div>
            : src ? <iframe ref={frameRef} title={title} src={`${src}#zoom=page-width&navpanes=0&toolbar=0`} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
            : <div style={{ padding: 30, color: '#6b7280', fontSize: 14 }}>Drawing the PDF…</div>}
        </div>
      </div>
    </div>
  );
}
